'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { PanelLeft } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { Button } from '../../components/button'
import { Separator } from '../../components/separator'
import { typographyVariants } from '../typography'
import { useSidebar } from './context'

// ─── Layout Components ───────────────────────────────────────────────────────

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='sidebar-header' className={cn('ui:flex ui:flex-col ui:gap-2 ui:p-2', className)} {...props} />
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-content'
      className={cn(
        'ui:flex ui:min-h-0 ui:flex-1 ui:flex-col ui:gap-2 ui:overflow-auto',
        'ui:group-data-[collapsible=icon]/sidebar:overflow-hidden',
        className,
      )}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='sidebar-footer' className={cn('ui:flex ui:flex-col ui:gap-2 ui:p-2', className)} {...props} />
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot='sidebar-separator'
      className={cn('ui:mx-2 ui:w-auto ui:data-[orientation=horizontal]:w-auto ui:bg-outline-variant/50', className)}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot='sidebar-inset'
      className={cn('ui:relative ui:flex ui:w-full ui:flex-1 ui:flex-col', className)}
      {...props}
    />
  )
}

function SidebarTrigger({ className, onClick, children, ...props }: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      data-slot='sidebar-trigger'
      variant='ghost'
      size='icon'
      className={className}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      {children ?? (
        <>
          <PanelLeft />
          <span className='ui:sr-only'>Toggle Sidebar</span>
        </>
      )}
    </Button>
  )
}

// ─── Group Components ────────────────────────────────────────────────────────

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group'
      className={cn('ui:relative ui:flex ui:w-full ui:min-w-0 ui:flex-col ui:p-2', className)}
      {...props}
    />
  )
}

function SidebarGroupLabel({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group-label'
      className={cn(
        'ui:flex ui:h-8 ui:shrink-0 ui:items-center ui:rounded-md ui:px-2 ui:text-xs ui:font-medium ui:opacity-70',
        'ui:group-data-[collapsible=icon]/sidebar:hidden',
        className,
      )}
      {...props}
    />
  )
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot='sidebar-group-content' className={cn('ui:w-full ui:text-sm', className)} {...props} />
}

// ─── Menu Components ─────────────────────────────────────────────────────────

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu'
      className={cn('ui:flex ui:w-full ui:min-w-0 ui:flex-col ui:gap-1', className)}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li data-slot='sidebar-menu-item' className={cn('ui:group/menu-item ui:relative', className)} {...props} />
}

interface SidebarMenuButtonProps extends React.ComponentProps<'button'> {
  asChild?: boolean
  isActive?: boolean
}

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  className,
  children,
  ...props
}: SidebarMenuButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot='sidebar-menu-button'
      data-active={isActive || undefined}
      className={cn(
        // Layout
        'ui:flex ui:w-full ui:items-center ui:gap-2 ui:overflow-hidden ui:rounded-md ui:px-3 ui:py-2 ui:text-left ui:text-sm ui:font-medium ui:outline-none ui:cursor-pointer',
        // Transitions
        'ui:transition-[width,height,padding] ui:duration-200',
        // SVG icon sizing
        'ui:[&>svg]:size-5 ui:[&>svg]:shrink-0',
        // Text truncation
        'ui:[&>span:last-child]:truncate',
        // Icon collapse mode - constrain to icon-only
        'ui:group-data-[collapsible=icon]/sidebar:size-10!',
        'ui:group-data-[collapsible=icon]/sidebar:p-2.5!',
        'ui:group-data-[collapsible=icon]/sidebar:justify-center',
        'ui:group-data-[collapsible=icon]/sidebar:gap-0!',
        typographyVariants({ variant: 'label-md' }),
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Sub-menu Components ─────────────────────────────────────────────────────

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu-sub'
      className={cn(
        'ui:mx-3.5 ui:flex ui:min-w-0 ui:translate-x-px ui:flex-col ui:gap-1 ui:border-l ui:px-2.5 ui:py-0.5',
        'ui:group-data-[collapsible=icon]/sidebar:hidden',
        className,
      )}
      {...props}
    />
  )
}

function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li data-slot='sidebar-menu-sub-item' className={cn('ui:group/menu-sub-item ui:relative', className)} {...props} />
  )
}

interface SidebarMenuSubButtonProps extends React.ComponentProps<'a'> {
  asChild?: boolean
  isActive?: boolean
}

function SidebarMenuSubButton({
  asChild = false,
  isActive = false,
  className,
  children,
  ...props
}: SidebarMenuSubButtonProps) {
  const Comp = asChild ? Slot : 'a'

  return (
    <Comp
      data-slot='sidebar-menu-sub-button'
      data-active={isActive || undefined}
      className={cn(
        'ui:flex ui:h-8 ui:min-w-0 ui:items-center ui:gap-2 ui:overflow-hidden ui:rounded-md ui:px-2 ui:text-sm ui:outline-none ui:cursor-pointer',
        'ui:[&>span:last-child]:truncate',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  )
}

export {
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarSeparator,
  SidebarInset,
  SidebarTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
}
